import { useCallback, useEffect, useRef } from 'react'
import { getBackendUrl } from '../lib/backendUrl'
import { useSetAtom, useAtomValue, useStore } from 'jotai'
import { toast } from 'sonner'
import { isStreamingAtom, currentSessionAtom, sessionsAtom } from '../state/store'
import { useSessions } from './useSessions'
import { useWebSocket } from '../contexts/WebSocketContext'
import { chatInputActionsRef } from '../components/ChatInput'
import type { BackendMessage, ChatAttachment, ChatMessage } from '../state/types'
import { buildStorageFileUrl, generateId, isSessionStreaming } from '../state/types'
import { backendMessageToChat } from '../state/types'

export function useChat() {
  const {
    currentSessionId,
    createSession,
    addMessage,
    updateActiveAssistantMessage,
    updateMessageById,
    deleteLastAssistantMessage,
  } = useSessions()
  const currentSession = useAtomValue(currentSessionAtom)
  const setIsStreaming = useSetAtom(isStreamingAtom)
  const store = useStore()
  const { sendUserMessage, addMessageHandler } = useWebSocket()

  // Track which chatId is currently streaming to route assistant messages correctly
  const streamingChatIdRef = useRef<string | null>(null)
  // One in-flight assistant message per chat. WS delta events do not include a
  // message id, so we anchor all assistant updates to the local placeholder id.
  const activeAssistantMessageIdsRef = useRef(new Map<string, string>())
  // Sessions where the user clicked "stop" — incoming deltas are ignored until
  // the next sendViaWS clears the entry (WebSocket keeps sending regardless)
  const stoppedSessionIdsRef = useRef(new Set<string>())

  const toRenderableAttachments = useCallback(
    (attachments: ChatAttachment[]) =>
      attachments.map((attachment) => {
        if (attachment.uploadStatus === 'uploaded' && (attachment.storageName || attachment.storageId)) {
          return {
            ...attachment,
            url: buildStorageFileUrl(attachment.storageName || attachment.storageId || ''),
          }
        }
        return attachment
      }),
    [],
  )

  useEffect(() => {
    setIsStreaming(isSessionStreaming(currentSession))
  }, [currentSession, setIsStreaming])

  // Register WebSocket handler for assistant streaming messages
  useEffect(() => {
    return addMessageHandler((msg) => {
      // Also check root-level fields for chatId (server may not nest under payload)
      const raw = msg as Record<string, unknown>

      if (msg.type === 'queue_status') {
        return
      }

      if (msg.type === 'user_message') {
        return
      }

      // Handle error before chatId check — server may send errors without a chatId
      if (msg.type === 'error') {
        const errorMsg = msg.payload?.message ?? (raw.message as string | undefined) ?? 'An error occurred'
        toast.error(errorMsg)
        const chatId =
          msg.payload?.chatId ??
          (raw.chatId as string | undefined) ??
          streamingChatIdRef.current
        if (chatId) {
          updateActiveAssistantMessage(chatId, (m) => ({
            ...m,
            isStreaming: false,
            isGeneratingImage: false,
          }))
          activeAssistantMessageIdsRef.current.delete(chatId)
        }
        setIsStreaming(false)
        streamingChatIdRef.current = null
        return
      }

      const chatId =
        msg.payload?.chatId ??
        (raw.chatId as string | undefined) ??
        streamingChatIdRef.current
      if (!chatId) return

      const getAssistantMessageId = () => activeAssistantMessageIdsRef.current.get(chatId) ?? null

      const hasAssistantMessage = (messageId: string) => {
        const sessions = store.get(sessionsAtom)
        const session = sessions.find((item) => item.id === chatId)
        return session?.messages.some((message) => message.id === messageId) ?? false
      }

      const createAssistantMessage = (partial?: Partial<ChatMessage>) => {
        const assistantId = partial?.id ?? generateId()
        activeAssistantMessageIdsRef.current.set(chatId, assistantId)
        addMessage(chatId, {
          id: assistantId,
          role: 'assistant',
          content: partial?.content ?? '',
          timestamp: partial?.timestamp ?? Date.now(),
          attachments: partial?.attachments,
          imageUrl: partial?.imageUrl,
          imageId: partial?.imageId,
          isGeneratingImage: partial?.isGeneratingImage ?? false,
          isStreaming: partial?.isStreaming ?? true,
        })
        return assistantId
      }

      const updateAssistantMessage = (updater: (message: ChatMessage) => ChatMessage) => {
        const existingId = getAssistantMessageId()
        if (existingId && hasAssistantMessage(existingId)) {
          updateMessageById(chatId, existingId, updater)
          return existingId
        }

        const assistantId = createAssistantMessage()
        updateMessageById(chatId, assistantId, updater)
        return assistantId
      }

      if (msg.type === 'assistant_delta') {
        // Incremental delta — append to last assistant message.
        // Skip if the user stopped generation for this session.
        if (stoppedSessionIdsRef.current.has(chatId)) return
        const delta = msg.payload?.delta ?? (raw.delta as string | undefined) ?? ''
        if (delta) {
          updateAssistantMessage((m) => ({ ...m, content: m.content + delta, isStreaming: true }))
        }
      } else if (msg.type === 'assistant_message') {
        // Assistant text payload for the current in-flight message.
        // The message is not considered complete until `assistant_done`.
        stoppedSessionIdsRef.current.delete(chatId)
        const serverContent = msg.payload?.content ?? (raw.content as string | undefined)
        const backendMessage =
          'role' in raw && raw.role === 'assistant'
            ? backendMessageToChat(raw as unknown as BackendMessage)
            : null
        updateAssistantMessage((m) => ({
          ...m,
          ...(backendMessage
            ? {
                ...backendMessage,
                id: m.id,
              }
            : null),
          content: serverContent || backendMessage?.content || m.content,
          isStreaming: true,
        }))
      } else if (msg.type === 'tool_use') {
        // Tool invocation — intentionally hidden for now
      } else if (msg.type === 'image_generation_progress') {
        // Image is being generated — show loading state on the assistant message
        updateAssistantMessage((m) => ({
          ...m,
          isGeneratingImage: true,
          isStreaming: true,
        }))
      } else if (msg.type === 'assistant_message_with_image') {
        // Assistant image payload for the current in-flight message.
        // Keep the message open until `assistant_done`.
        const image = raw.image as Record<string, unknown> | undefined
        const rawBlobUrl =
          msg.payload?.blobUrl ??
          image?.blobUrl as string | undefined ??
          (raw.blobUrl as string | undefined) ??
          ''
        const imageId =
          msg.payload?.id ??
          image?.id as string | undefined ??
          (raw.id as string | undefined) ??
          ''
        const content = msg.payload?.content ?? (raw.content as string | undefined) ?? ''
        // Prepend backend URL if blobUrl is a relative path
        const imageUrl = rawBlobUrl && rawBlobUrl.startsWith('/') ? `${getBackendUrl()}${rawBlobUrl}` : rawBlobUrl
        updateAssistantMessage((m) => ({
          ...m,
          content: content || m.content,
          imageUrl,
          imageId,
          isGeneratingImage: false,
          isStreaming: true,
        }))
      } else if (msg.type === 'result') {
        const isSuccess =
          msg.payload?.success ??
          (raw.success as boolean | undefined) ??
          false

        if (!isSuccess) {
          return
        }

        stoppedSessionIdsRef.current.delete(chatId)
        updateAssistantMessage((m) => ({ ...m, isStreaming: false, isGeneratingImage: false }))
        activeAssistantMessageIdsRef.current.delete(chatId)
        setIsStreaming(false)
        streamingChatIdRef.current = null
      }
    })
  }, [addMessage, addMessageHandler, setIsStreaming, updateActiveAssistantMessage, updateMessageById])

  /** Internal: add assistant placeholder and send via WebSocket */
  const sendViaWS = useCallback(
    (sessionId: string, userContent: string, attachments: ChatAttachment[]) => {
      // Clear any previous stop signal for this session before sending
      stoppedSessionIdsRef.current.delete(sessionId)
      // Add empty assistant placeholder
      const assistantId = generateId()
      activeAssistantMessageIdsRef.current.set(sessionId, assistantId)
      addMessage(sessionId, {
        id: assistantId,
        role: 'assistant',
        content: '',
        timestamp: Date.now(),
        isStreaming: true,
      })

      setIsStreaming(true)
      streamingChatIdRef.current = sessionId
      sendUserMessage(
        sessionId,
        userContent,
        attachments
          .filter((attachment) => attachment.uploadStatus === 'uploaded' && attachment.storageId)
          .map((attachment) => ({
            storageId: attachment.storageId as string,
            filename: attachment.name,
            mimeType: attachment.type,
            storageName: attachment.storageName,
          })),
      )
    },
    [addMessage, setIsStreaming, sendUserMessage],
  )

  const sendMessage = useCallback(
    async (content: string, attachments: ChatAttachment[] = []) => {
      let sessionId = currentSessionId

      if (!sessionId) {
        const session = await createSession()
        sessionId = session.id
      }

      const renderableAttachments = toRenderableAttachments(attachments)

      // Optimistic user message
      const userMsg: ChatMessage = {
        id: generateId(),
        role: 'user',
        content,
        timestamp: Date.now(),
        attachments: renderableAttachments,
      }
      addMessage(sessionId, userMsg)

      sendViaWS(sessionId, content, renderableAttachments)
    },
    [currentSessionId, createSession, addMessage, sendViaWS, toRenderableAttachments],
  )

  /** Regenerate: resend the last user message content via WebSocket */
  const regenerate = useCallback(() => {
    const session = store.get(currentSessionAtom)
    if (!session || session.messages.length === 0) return

    const sessionId = session.id
    const lastMsg = session.messages[session.messages.length - 1]

    // Remove last assistant message locally if present
    if (lastMsg.role === 'assistant') {
      deleteLastAssistantMessage(sessionId)
    }

    // Find the last user message to resend
    const lastUserMsg = [...session.messages]
      .reverse()
      .find((m) => m.role === 'user')
    if (!lastUserMsg) return

    sendViaWS(sessionId, lastUserMsg.content, toRenderableAttachments(lastUserMsg.attachments ?? []))
  }, [store, deleteLastAssistantMessage, sendViaWS, toRenderableAttachments])

  const stopGeneration = useCallback(() => {
    // Stop locally — no signal sent to backend; the server will keep streaming.
    // Mark the session as stopped so the message handler ignores incoming deltas.
    if (streamingChatIdRef.current) {
      stoppedSessionIdsRef.current.add(streamingChatIdRef.current)
      updateActiveAssistantMessage(streamingChatIdRef.current, (m) => ({ ...m, isStreaming: false }))
    }
    streamingChatIdRef.current = null
    setIsStreaming(false)
  }, [setIsStreaming, updateActiveAssistantMessage])

  // Wire up ChatInput's action ref
  useEffect(() => {
    chatInputActionsRef.current = {
      onSend: (content: string, attachments: ChatAttachment[]) => void sendMessage(content, attachments),
      onStop: stopGeneration,
    }
  }, [sendMessage, stopGeneration])

  return { sendMessage, stopGeneration, regenerate }
}
